<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <form @submit="createIndex" class="flex flex-col h-full">
      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full">
          <div class="border-solid border-b border-0 border-gray-border px-8 pb-8">
            <h3 class="font-semibold mt-8 truncate">
              <span class="text-gray-dark">{{ groupName }}</span>
              <span class="text-gray ml-1">/</span>
              <span class="text-black ml-1">Add market</span>
            </h3>
          </div>

          <template v-if="hasAvailableMarkets">
            <settings-bot-market-form v-model="marketForm"
                                      :indexes="indexes"
                                      :exclude-index-group-id="indexGroupId"
                                      :existing-only="true"
                                      content-class="px-8 pb-3"
            />

            <div class="mt-3 border-solid border-0 border-t border-gray-border pt-4">
              <div class="px-8">
                <with-error-message :show-error="isIndexSourceNameError">
                  <base-label id="indexSourceName" label="Source Name" :label-classes="'font-medium'"/>
                  <base-input id="indexSourceName"
                              name="indexSourceName"
                              placeholder="e.g. Sales campaign feed"
                              variant="editor"
                              type="string"
                              class="w-full border-gray-border py-px mt-1 h-10"
                              :class="{'border-focus-error': isIndexSourceNameError}"
                              v-model.trim="indexSourceName"
                  />
                </with-error-message>

                <div class="mt-5">
                  <base-label label="Data source"
                              :label-classes="'font-medium'"
                              id="data-source"
                  />
                  <BaseToggleGroup class="mt-1" :tabs="formsOptions" v-model="sourceType" />
                  <settings-indexes-forms class="mt-4"
                                          :fileSettings.sync="fileSettings"
                                          :active-form.sync="sourceType"
                                          :xmlFeedSettings.sync="xmlFeedSettings"
                                          :webPageSettings.sync="webPageSettings"
                                          :show-field-selection="showFieldSelection"
                                          @update:available-tags="availableTags = $event"
                  />
                </div>
              </div>

              <settings-index-scheduler :schedule-params="scheduleParams"
                                        :is-create="true"
                                        @update-schedule-params="updateScheduleParams"
                                        class="mt-6 pt-4 px-8 pb-8 border-solid border-t border-0 border-gray-border"
              />
            </div>
          </template>

          <p v-else class="mt-8 px-8 text-gray-dark">
            No available markets for "{{ groupName }}" knowledge base group
          </p>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-4 mb-6 px-8 flex-shrink-0">
        <template v-if="hasAvailableMarkets">
          <busy-button id="addIndexMarketCreateButton"
                       :variant="createButtonVariant"
                       class="col-span-1"
                       :is-loading="isLoading"
                       :is-disabled="isCreateDisabled"
                       @click="createIndex"
          >
            <span class="w-full">{{ createButtonText }}</span>
          </busy-button>
          <base-button variant="bordered"
                       class="col-span-1 justify-center"
                       :disabled="isLoading"
                       @click="$emit('cancel')"
          >
            Cancel
          </base-button>
        </template>
        <template v-else>
          <base-button id="addIndexMarketGoToBotsButton"
                       variant="primary"
                       class="col-span-1 justify-center"
                       :router-link="botSettingsRoute"
          >
            Add Market
          </base-button>
          <base-button variant="bordered"
                       class="col-span-1 justify-center"
                       @click="$emit('cancel')"
          >
            Cancel
          </base-button>
        </template>
      </div>
    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseButton from '@/components/base-components/BaseButton.vue'
import SettingsBotMarketForm from '@/components/settings/bots/SettingsBotMarketForm.vue'
import SettingsIndexesForms from '@/components/settings/indexes/SettingsIndexesForms.vue'
import SettingsIndexScheduler from '@/components/settings/indexes/SettingsIndexScheduler.vue'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import mixins from '@/utils/mixins'
import indexCreateMixin from '@/utils/index-create.mixin'
import {
  createDefaultMarketForm,
  isMarketFormValid,
  resolveMarketId
} from '@/utils/market.utils'
import { getAvailableMarketOptionsForIndexGroup } from '@/utils/index.utils'
import { extractCategoryTag, extractFieldSelection } from '@/utils/xml-source-config.utils'
import { KNOWLEDGE_BASES } from '@/utils/knowledgeBase.constants'
import { createDefaultProviderForm } from '@/utils/index-provider.utils'
import { ROUTE_NAMES } from '@/router'

export default {
  name: 'SettingsIndexesAddMarketEditor',

  mixins: [mixins, indexCreateMixin],

  components: {
    BusyButton,
    BaseButton,
    SettingsBotMarketForm,
    SettingsIndexesForms,
    SettingsIndexScheduler,
    WithErrorMessage
  },

  props: {
    groupName: {
      type: String,
      required: true
    },
    indexGroupId: {
      type: String,
      required: true
    },
    indexes: {
      type: Array,
      default: () => []
    }
  },

  emits: ['cancel', 'create'],

  data() {
    return {
      marketForm: createDefaultMarketForm(),
      providerForm: createDefaultProviderForm(),
      availableTags: []
    }
  },

  // Markets in a group share one feed structure (same fields, different language/URL). Inherit the
  // source config — provider/embedding model, tag mapping, and the Qdrant field selection — from an
  // existing market so adding a market only needs the new market + feed URL.
  created() {
    const sibling = (this.indexes || []).find(index =>
      index.index_group_id === this.indexGroupId && index.sources && index.sources.length > 0
    )
    if (!sibling) return

    const source = sibling.sources[0]
    this.providerForm = sibling.provider === 'qdrant' ?
      createDefaultProviderForm(sibling.provider_params) :
      createDefaultProviderForm()
    this.indexSourceName = source.name

    if (source.type === 'url') {
      this.sourceType = KNOWLEDGE_BASES.XML_FEED
      const config = source.config || {}
      this.xmlFeedSettings = {
        url: '', // the one thing the user must provide for the new market
        item: config.tag_mapping?.item || '',
        itemName: config.tag_mapping?.item_name || '',
        itemId: config.tag_mapping?.item_id || '',
        category: extractCategoryTag(config),
        // Inheriting the sibling market's schema IS manual configuration — that is the entire point
        // of this editor. Without it buildXmlSource would send the url alone and the engine would
        // re-derive, silently discarding the group's agreed mapping and field selection.
        manualConfig: !!config.tag_mapping?.item,
        fieldSelection: extractFieldSelection(config),
        // The sibling's tags ARE this market's tags — same feed structure, different language. Without
        // them the field table has nothing to offer: the new source has not been indexed yet, and the
        // UI no longer downloads the feed to find out for itself (PRO-658).
        fieldSamples: source.field_samples || null
      }
    }
  },

  computed: {
    hasAvailableMarkets() {
      return getAvailableMarketOptionsForIndexGroup(this.indexes, this.indexGroupId).length > 0
    },

    botSettingsRoute() {
      return { name: ROUTE_NAMES.bots }
    },

    isMarketFormValid() {
      return isMarketFormValid(this.marketForm, this.hasAvailableMarkets)
    },

    isCreateDisabled() {
      return this.isLoading || !this.isMarketFormValid
    },

    // editable field-selection table for a qdrant XML market, shown once the new feed's preview loads
    showFieldSelection() {
      return this.providerForm.provider === 'qdrant' &&
        this.sourceType === KNOWLEDGE_BASES.XML_FEED &&
        this.availableTags.length > 0
    }
  },

  methods: {
    updateFieldSelection(fieldSelection) {
      this.xmlFeedSettings = { ...this.xmlFeedSettings, fieldSelection }
    },

    async resolveIndexCreateParams() {
      const { marketId, marketName } = await resolveMarketId(this.marketForm)
      return {
        indexName: marketName,
        extraParams: {
          index_group_id: this.indexGroupId,
          market_id: marketId,
          // carry the inherited provider/embedding model so the new market matches its siblings
          ...this.providerForm
        }
      }
    },

    emitIndexCreated(newIndex) {
      this.$emit('create', newIndex)
    }
  }
}
</script>
